import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { risk_portal_public_view, risk_portal_public_update, risk_portal_public_acknowledge } from '../../services';
import './RiskPortal.css';

const RATING_COLORS = { Low: '#19A07E', Medium: '#D9BF21', High: '#DE9E30', Critical: '#D24646' };

export default function RiskOwnerView() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [risk, setRisk] = useState(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [declineReason, setDeclineReason] = useState('');

    const [form, setForm] = useState({
        treatment_plan: '', treatment_option: '', controls_applied: '',
        residual_risk: '', state: '', comment: '',
    });

    useEffect(() => { loadRisk(); }, [token]);

    const loadRisk = async () => {
        setLoading(true);
        try {
            const res = await risk_portal_public_view(token);
            const d = res?.data?.response_data?.data || res?.statusCode?.response_data?.data || res?.data?.data;
            if (d) {
                setRisk(d);
                if (d.ack_status === 'acknowledged') setAcknowledged(true);
                setForm({
                    treatment_plan: d.treatment_plan || '',
                    treatment_option: d.treatment_option || '',
                    controls_applied: d.controls_applied || '',
                    residual_risk: d.residual_risk || '',
                    state: d.state || 'open',
                    comment: '',
                });
            } else {
                setError('Unable to load risk data.');
            }
        } catch (e) {
            const msg = e?.response?.data?.response_data?.message || e?.response?.data?.error_msg || 'Invalid or expired link.';
            setError(msg);
        }
        setLoading(false);
    };

    const handleUpdate = async () => {
        setSaving(true);
        try {
            await risk_portal_public_update(token, form);
            setMessage('Risk updated successfully.');
            setForm(f => ({ ...f, comment: '' }));
            loadRisk();
            setTimeout(() => setMessage(''), 4000);
        } catch (e) {
            const msg = e?.response?.data?.response_data?.message || e?.response?.data?.error_msg || 'Update failed.';
            setMessage(msg);
        }
        setSaving(false);
    };

    const handleAcknowledge = async () => {
        try {
            await risk_portal_public_acknowledge(token, { action: 'acknowledge' });
            setAcknowledged(true);
            setMessage('Risk ownership acknowledged. Further changes will be managed through the workflow stages in the dashboard.');
            setTimeout(() => setMessage(''), 5000);
        } catch (e) { setMessage('Acknowledgement failed.'); }
    };

    const handleDecline = async () => {
        try {
            await risk_portal_public_acknowledge(token, { action: 'decline', reason: declineReason });
            setMessage('Risk assignment declined. The risk manager will be notified.');
            setShowDeclineModal(false);
        } catch (e) { setMessage('Decline failed.'); }
    };

    if (loading) return (
        <div className="rov-page">
            <div className="rov-loading"><div className="rp-spinner" /><p>Loading risk...</p></div>
        </div>
    );

    if (error) return (
        <div className="rov-page">
            <div className="rov-error">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                <h2>Link Unavailable</h2>
                <p>{error}</p>
            </div>
        </div>
    );

    const r = risk;
    const a = r.assessment;
    const canEdit = r.permission === 'update';

    return (
        <div className="rov-page">
            <div className="rov-header">
                <div className="rov-header-left">
                    <span className="rov-logo">FORESEE SECURITY</span>
                    <span className="rov-header-title">Risk Owner Portal</span>
                </div>
                <div className="rov-header-right">
                    {r.recipient_name && <span className="rov-user">{r.recipient_name}</span>}
                    <span className="rov-perm">{canEdit ? 'View & Update' : 'View Only'}</span>
                </div>
            </div>

            <div className="rov-content">
                {message && <div className="rp-flash">{message}</div>}

                <div className="rov-risk-header">
                    <h1><span className="rp-risk-id-lg">{r.risk_id}</span> {r.risk}</h1>
                    <div className="rp-detail-meta">
                        <span className="rp-state-badge rp-state-badge-lg">{(r.state || '').replace(/_/g, ' ')}</span>
                        <span className="rp-type-badge">{(r.risk_type || '').replace(/_/g, ' ')}</span>
                        <span className="rp-score-badge" style={{ background: RATING_COLORS[r.severity === 'critical' ? 'Critical' : r.severity === 'high' ? 'High' : r.severity === 'medium' ? 'Medium' : 'Low'] }}>{r.severity}</span>
                        {r.risk_owner && <span className="rp-owner-badge">Owner: {r.risk_owner}</span>}
                        {r.target_date && <span className="rp-date-badge">Target: {r.target_date}</span>}
                    </div>
                    {r.shared_by && <p style={{ fontSize: 11, color: 'var(--rp-text-3)', marginTop: 6 }}>Shared by: {r.shared_by}{r.expires_at ? ` | Link expires: ${r.expires_at.split('T')[0]}` : ''}</p>}
                </div>

                {canEdit && !acknowledged && (
                    <div className="rov-ack-banner">
                        <div className="rov-ack-icon">!</div>
                        <div className="rov-ack-content">
                            <strong>Risk Assignment: Acknowledgement Required</strong>
                            <p>You have been assigned as the owner of this risk. Please review the details below and acknowledge that you accept ownership and responsibility for managing this risk.</p>
                            <div className="rov-ack-actions">
                                <button className="rp-btn rp-btn-primary" onClick={handleAcknowledge}>I Acknowledge & Accept Ownership</button>
                                <button className="rp-btn" style={{ borderColor: '#D24646', color: '#D24646' }} onClick={() => setShowDeclineModal(true)}>Decline Assignment</button>
                            </div>
                        </div>
                    </div>
                )}

                {acknowledged && (
                    <div className="rov-ack-confirmed">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#19A07E" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        <div>
                            <span>Ownership acknowledged. Risk details are now locked.</span>
                            <p style={{ margin: '6px 0 0', fontSize: 12 }}>
                                To create treatment plans and advance the workflow, go to the <a href={`${process.env.PUBLIC_URL}/risk-portal`} style={{ color: '#1976d2', fontWeight: 600 }}>Risk Registry Portal</a> and open this risk from the Risk Register.
                            </p>
                        </div>
                    </div>
                )}

                {showDeclineModal && (
                    <div className="rp-modal-overlay" onClick={() => setShowDeclineModal(false)}>
                        <div className="rp-modal" onClick={e => e.stopPropagation()}>
                            <div className="rp-modal-header"><h3>Decline Risk Assignment</h3><button className="rp-modal-close" onClick={() => setShowDeclineModal(false)}>×</button></div>
                            <div className="rp-modal-body">
                                <p style={{ fontSize: 12, color: 'var(--rp-text-3)', marginBottom: 12 }}>Please provide a reason for declining. The risk manager will be notified and may reassign this risk.</p>
                                <div className="rp-form-group">
                                    <label>Reason for declining</label>
                                    <textarea className="rp-textarea" rows={3} value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="Why are you declining this risk assignment?" />
                                </div>
                            </div>
                            <div className="rp-modal-footer">
                                <button className="rp-btn" onClick={() => setShowDeclineModal(false)}>Cancel</button>
                                <button className="rp-btn" style={{ background: '#D24646', color: '#fff', border: 'none' }} onClick={handleDecline}>Decline Assignment</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="rov-grid">
                    <div className="rp-card">
                        <h3>RISK DETAILS</h3>
                        <div className="rov-field"><span className="rov-label">Threat</span><span>{r.threat || '-'}</span></div>
                        <div className="rov-field"><span className="rov-label">Vulnerability</span><span>{r.vulnerability || '-'}</span></div>
                        <div className="rov-field"><span className="rov-label">Likelihood</span><span>{r.likelihood}/5</span></div>
                        <div className="rov-field"><span className="rov-label">Impact</span><span>{r.impact}/5</span></div>
                        <div className="rov-field"><span className="rov-label">Risk Score</span><span className="rp-score-badge" style={{ background: RATING_COLORS[r.severity === 'critical' ? 'Critical' : r.severity === 'high' ? 'High' : r.severity === 'medium' ? 'Medium' : 'Low'] }}>{r.risk_score}</span></div>
                    </div>
                    {a && (
                        <div className="rp-card">
                            <h3>PORTAL ASSESSMENT</h3>
                            <div className="rp-assessment-grid">
                                <div className="rp-assess-card">
                                    <h4>Inherent</h4>
                                    <div className="rp-assess-score" style={{ background: RATING_COLORS[a.inherent_rating] }}>{a.inherent_score}</div>
                                    <div className="rp-assess-rating" style={{ color: RATING_COLORS[a.inherent_rating] }}>{a.inherent_rating}</div>
                                </div>
                                <div className="rp-assess-card">
                                    <h4>Residual</h4>
                                    <div className="rp-assess-score" style={{ background: RATING_COLORS[a.residual_rating] }}>{a.residual_score}</div>
                                    <div className="rp-assess-rating" style={{ color: RATING_COLORS[a.residual_rating] }}>{a.residual_rating}</div>
                                </div>
                            </div>
                            <div className="rov-field" style={{ marginTop: 8 }}><span className="rov-label">Velocity</span><span>{a.risk_velocity}</span></div>
                        </div>
                    )}
                </div>

                {r.controls?.length > 0 && (
                    <div className="rp-card">
                        <h3>MAPPED CONTROLS</h3>
                        <table className="rp-table rp-table-sm">
                            <thead><tr><th>Control</th><th>Type</th><th>Effectiveness</th></tr></thead>
                            <tbody>
                                {r.controls.map((c, i) => <tr key={i}><td>{c.control_name}</td><td>{c.control_type}</td><td>{c.effectiveness}%</td></tr>)}
                            </tbody>
                        </table>
                    </div>
                )}

                {r.treatments?.length > 0 && (
                    <div className="rp-card">
                        <h3>TREATMENT PLANS</h3>
                        {r.treatments.map((tp, i) => (
                            <div key={i} className="rp-treatment-card">
                                <div className="rp-treatment-header">
                                    <span className="rp-treatment-strategy">{tp.strategy}</span>
                                    <span className={`rp-treatment-status rp-treatment-status-${tp.status}`}>{(tp.status || '').replace(/_/g, ' ')}</span>
                                </div>
                                <p style={{ fontSize: 12, color: 'var(--rp-text-2)' }}>{tp.description}</p>
                                {tp.actions?.length > 0 && (
                                    <table className="rp-table rp-table-sm">
                                        <thead><tr><th>Action</th><th>Owner</th><th>Priority</th><th>Due</th><th>Status</th></tr></thead>
                                        <tbody>
                                            {tp.actions.map((act, j) => (
                                                <tr key={j}><td>{act.title}</td><td>{act.owner_email}</td><td>{act.priority}</td><td>{act.due_date}</td><td>{(act.status || '').replace(/_/g, ' ')}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {canEdit && !acknowledged && (
                    <div className="rp-card">
                        <h3>UPDATE RISK</h3>
                        <p style={{ fontSize: 12, color: 'var(--rp-text-3)', marginBottom: 12 }}>
                            As the risk owner, you can update the treatment plan and add comments. State changes are managed through the workflow.
                        </p>
                        <div className="rp-form-group">
                            <label>Treatment Plan</label>
                            <textarea className="rp-textarea" rows={3} value={form.treatment_plan} onChange={e => setForm(p => ({ ...p, treatment_plan: e.target.value }))} placeholder="Describe your mitigation approach..." />
                        </div>
                        <div className="rp-form-row">
                            <div className="rp-form-group">
                                <label>Controls Applied</label>
                                <input className="rp-input" value={form.controls_applied} onChange={e => setForm(p => ({ ...p, controls_applied: e.target.value }))} />
                            </div>
                            <div className="rp-form-group">
                                <label>Residual Risk</label>
                                <input className="rp-input" value={form.residual_risk} onChange={e => setForm(p => ({ ...p, residual_risk: e.target.value }))} />
                            </div>
                        </div>
                        <div className="rp-form-group">
                            <label>Add Comment</label>
                            <textarea className="rp-textarea" rows={2} value={form.comment} onChange={e => setForm(p => ({ ...p, comment: e.target.value }))} placeholder="Add a note or update..." />
                        </div>
                        <button className="rp-btn rp-btn-primary" onClick={handleUpdate} disabled={saving}>
                            {saving ? 'Saving...' : 'Update Risk'}
                        </button>
                    </div>
                )}

                {!canEdit && (
                    <div className="rp-card" style={{ textAlign: 'center', padding: 24 }}>
                        <p style={{ color: 'var(--rp-text-3)', fontSize: 13 }}>You have view-only access to this risk. Contact the risk manager to request update permissions.</p>
                    </div>
                )}

                <div className="rov-footer">
                    <p>Foresee Security Risk Registry | This is a secure, token-based link. Do not share with unauthorized individuals.</p>
                </div>
            </div>
        </div>
    );
}
